import type { InfiniteData } from '@tanstack/react-query';
import type { QueryKey as RqQueryKey } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { orderBy } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import type {
  ListingStage,
  ProjectListingCountResponse,
} from '@/api/v4/project-listings.api';
import { updateProjectListingStage } from '@/api/v4/project-listings.api';
import type { ProjectListingResponse } from '@/api/v4/project-listings.api';
import type {
  ProjectCompaniesSort,
  ProjectListing,
  ProjectListingsResponse,
} from '@/api/v4/projects.api';
import { queryClient, QueryKey } from '@/config/query-client';
import {
  addPendingProjectListingStage,
  removePendingProjectListingStage,
} from '@/state/project-listing-pending-stage.state';
import { notify } from '@/ui/snackbar/notify';
import type { ISort } from '@/ui/table/table.types';

import { isDealStage, ListingStageName } from '../../listing-stage/types';
import { projectIdState } from '../project.state';

import { readProjectListingsQueryMeta } from './project-listings-query-meta';

interface UpdateProjectListingStageParams {
  newProjectListingStageId: string;
  onSuccess?: () => void;
  projectListingId: string;
  withNotification?: boolean;
}

interface MutationVariables {
  newProjectListingStageId: string;
  onSuccess?: () => void;
  projectListingId: string;
  withNotification?: boolean;
}

interface MutationRollbackContext {
  companyName?: string;
  countQueriesSnapshot: ReadonlyArray<
    [RqQueryKey, ProjectListingCountResponse | undefined]
  >;
  infiniteQueriesSnapshot: ReadonlyArray<
    [RqQueryKey, InfiniteData<ProjectListingsResponse | null> | undefined]
  >;
  listingByIdSnapshot: readonly [
    RqQueryKey,
    ProjectListingResponse | undefined,
  ];
  organizationId?: string;
  refetchTargets: ReadonlyArray<{ pageIndex: number; queryKey: RqQueryKey }>;
}

const OPTIMISTIC_SORT_KEYS: ReadonlyArray<ProjectCompaniesSort> = [
  'averageRating',
  'capitalRaised',
  'companyName',
  'status',
  'yearEstablished',
];

const isSortQueryKeyFragment = (
  value: unknown,
): value is ISort<ProjectCompaniesSort> => {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    typeof record.sort === 'string' &&
    (record.direction === 'asc' || record.direction === 'desc')
  );
};

const readSortFromProjectListingsQueryKey = (
  queryKey: RqQueryKey,
): ISort<ProjectCompaniesSort> | undefined => {
  if (queryKey[0] !== QueryKey.ProjectListings) {
    return undefined;
  }

  for (let index = 3; index < queryKey.length; index += 1) {
    const fragment = queryKey[index];

    if (isSortQueryKeyFragment(fragment)) {
      return fragment;
    }
  }

  return undefined;
};

const buildStatusSortOrderById = (
  listingStages: ListingStage[],
): ReadonlyMap<string, number> =>
  new Map(listingStages.map(stage => [stage.id, stage.sortOrder]));

const getOptimisticSortComparable = (
  listing: ProjectListing,
  sort: ProjectCompaniesSort,
  statusSortOrderById?: ReadonlyMap<string, number>,
): string | number | null | undefined => {
  switch (sort) {
    case 'averageRating':
      return listing.organization.averageRating;
    case 'capitalRaised':
      return listing.organization.capitalRaised;
    case 'companyName':
      return listing.organization.name;
    case 'status':
      return (
        statusSortOrderById?.get(listing.status.id) ?? listing.status.sortOrder
      );
    case 'yearEstablished':
      return listing.organization.yearEstablished;
    default:
      return undefined;
  }
};

const optimisticallyReorderPageRecordsIfApplicable = (
  queryKey: RqQueryKey,
  records: ProjectListing[],
  listingStages: ListingStage[],
): ProjectListing[] => {
  const sortParams = readSortFromProjectListingsQueryKey(queryKey);

  if (!sortParams || !OPTIMISTIC_SORT_KEYS.includes(sortParams.sort)) {
    return records;
  }

  const statusSortOrderById =
    sortParams.sort === 'status'
      ? buildStatusSortOrderById(listingStages)
      : undefined;

  if (sortParams.sort === 'status') {
    return orderBy(
      records,
      [
        listing =>
          getOptimisticSortComparable(listing, 'status', statusSortOrderById),
        listing => listing.organization.name ?? '',
      ],
      [sortParams.direction, 'asc'],
    );
  }

  return orderBy(
    records,
    listing =>
      getOptimisticSortComparable(
        listing,
        sortParams.sort,
        statusSortOrderById,
      ),
    sortParams.direction,
  );
};

const findListingStatusIdInInfiniteCaches = (
  cachedSeries: ReadonlyArray<
    InfiniteData<ProjectListingsResponse | null> | undefined
  >,
  projectListingId: string,
): string | undefined => {
  for (const cache of cachedSeries) {
    if (!cache?.pages) {
      continue;
    }

    for (const page of cache.pages) {
      const hit = page?.records.find(
        listing => listing.id === projectListingId,
      );
      if (hit) {
        return hit.status.id;
      }
    }
  }

  return undefined;
};

const findListingOrgFromInfiniteCaches = (
  cachedSeries: ReadonlyArray<
    InfiniteData<ProjectListingsResponse | null> | undefined
  >,
  projectListingId: string,
): { companyName: string; organizationId: string } | undefined => {
  for (const cache of cachedSeries) {
    if (!cache?.pages) {
      continue;
    }

    for (const page of cache.pages) {
      const hit = page?.records.find(
        listing => listing.id === projectListingId,
      );
      if (hit) {
        return {
          companyName: hit.organization.name,
          organizationId: hit.organization.id,
        };
      }
    }
  }

  return undefined;
};

const isStageNotRelevant = (stage: ListingStage): boolean =>
  stage.name === ListingStageName.notRelevant;

const withUpdatedListingStage = (
  listing: ProjectListing,
  newStage: ListingStage,
): ProjectListing => ({
  ...listing,
  notRelevant: isStageNotRelevant(newStage),
  status: {
    displayName: newStage.displayName,
    id: newStage.id,
    name: newStage.name,
    sortOrder: newStage.sortOrder,
  },
});

const optimisticAdjustStagesCount = ({
  draft,
  fromStageId,
  toStageId,
}: {
  draft: ProjectListingCountResponse;
  fromStageId: string;
  toStageId: string;
}): ProjectListingCountResponse => {
  if (fromStageId === toStageId) {
    return draft;
  }

  return {
    ...draft,
    stagesCount: draft.stagesCount.map(stageCount => {
      if (stageCount.stageId === fromStageId) {
        return { ...stageCount, count: Math.max(0, stageCount.count - 1) };
      }
      if (stageCount.stageId === toStageId) {
        return { ...stageCount, count: stageCount.count + 1 };
      }
      return stageCount;
    }),
  };
};

const revertMutationCaches = (
  rollback: MutationRollbackContext | undefined,
): void => {
  if (!rollback) {
    return;
  }

  rollback.infiniteQueriesSnapshot.forEach(([key, snapshot]) => {
    queryClient.setQueryData(key, snapshot);
  });

  rollback.countQueriesSnapshot.forEach(([key, snapshot]) => {
    queryClient.setQueryData(key, snapshot);
  });

  const [listingQueryKey, snapshotListingById] = rollback.listingByIdSnapshot;

  queryClient.setQueryData(listingQueryKey, snapshotListingById);
};

const serializeQueryKey = (queryKey: RqQueryKey): string =>
  JSON.stringify(queryKey);

const invalidateCriticalProjectListingQueries = async ({
  projectId,
  refetchTargets,
}: {
  projectId: string;
  refetchTargets: ReadonlyArray<{ pageIndex: number; queryKey: RqQueryKey }>;
}): Promise<void> => {
  const refetchTargetKeyStrings = new Set(
    refetchTargets.map(({ queryKey }) => serializeQueryKey(queryKey)),
  );

  for (const { queryKey } of refetchTargets) {
    await queryClient.invalidateQueries({ queryKey });
  }

  await queryClient.invalidateQueries({
    predicate: query => {
      if (
        query.queryKey[0] !== QueryKey.ProjectListings ||
        query.queryKey[1] !== projectId
      ) {
        return false;
      }

      return !refetchTargetKeyStrings.has(
        serializeQueryKey(query.queryKey as RqQueryKey),
      );
    },
  });

  await queryClient.invalidateQueries({
    predicate: query =>
      query.queryKey[0] === QueryKey.ProjectListingsCount &&
      query.queryKey[1] === projectId,
  });
};

const invalidateBackgroundProjectListingQueries = ({
  organizationId,
  projectId,
}: {
  organizationId: string | undefined;
  projectId: string | null | undefined;
}): void => {
  if (!projectId) return;

  void queryClient.invalidateQueries({
    queryKey: [QueryKey.Project, projectId],
  });
  void queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] });
  void queryClient.invalidateQueries({
    queryKey: [QueryKey.LastActivitiesCount],
  });
  void queryClient.invalidateQueries({ queryKey: [QueryKey.Snapshot] });
  void queryClient.invalidateQueries({ queryKey: [QueryKey.Funnel] });
  void queryClient.invalidateQueries({
    queryKey: [QueryKey.Projects],
    refetchType: 'none',
  });
  void queryClient.invalidateQueries({ queryKey: [QueryKey.ProjectOptions] });

  if (organizationId) {
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.CompanyProjectsListings, organizationId],
    });
  }

  void queryClient.invalidateQueries({
    queryKey: [QueryKey.ProjectUpdates, projectId],
  });
};

export const useUpdateProjectListingStage = (): {
  editProjectListingStage: (
    params: UpdateProjectListingStageParams,
  ) => Promise<void>;
} => {
  const { t } = useTranslation('projects');
  const projectId = useAtomValue(projectIdState);

  const getListingStages = (): ListingStage[] => {
    return (
      queryClient.getQueryData<ListingStage[]>([QueryKey.ListingStages]) ?? []
    );
  };

  const notifyAboutChangedStage = (
    companyName: string,
    newStage: ListingStage,
  ): void => {
    const newStageName = isDealStage(newStage.name)
      ? t`deal`
      : (newStage.displayName ?? newStage.name);

    notify({
      message: t('listings.movedToStage', {
        companyName,
        statusName: newStageName,
      }),
    });
  };

  const mutation = useMutation<
    void,
    Error,
    MutationVariables,
    MutationRollbackContext | undefined
  >({
    mutationFn: async ({
      newProjectListingStageId,
      projectListingId,
    }: MutationVariables): Promise<void> => {
      await updateProjectListingStage(
        projectListingId,
        newProjectListingStageId,
      );
    },
    onError: (_err, _variables, rollback) => {
      revertMutationCaches(rollback);

      if (!rollback?.companyName) {
        return;
      }

      notify({
        message: t('listings.stageUpdateFailed', {
          companyName: rollback.companyName,
        }),
        variant: 'error',
      });
    },
    onMutate: async ({
      newProjectListingStageId,
      projectListingId,
      withNotification = true,
    }: MutationVariables): Promise<MutationRollbackContext | undefined> => {
      addPendingProjectListingStage(projectListingId);

      if (!projectId) return undefined;

      const listingStages = getListingStages();
      const newStage = listingStages.find(
        stage => stage.id === newProjectListingStageId,
      );

      await queryClient.cancelQueries({
        predicate: query => {
          const key = query.queryKey;
          return key[0] === QueryKey.ProjectListings && key[1] === projectId;
        },
      });

      const infiniteQueriesSnapshot = queryClient.getQueriesData<
        InfiniteData<ProjectListingsResponse | null> | undefined
      >({
        predicate: query =>
          query.queryKey[0] === QueryKey.ProjectListings &&
          query.queryKey[1] === projectId,
      });

      const countQueriesSnapshot = queryClient.getQueriesData<
        ProjectListingCountResponse | undefined
      >({
        predicate: query =>
          query.queryKey[0] === QueryKey.ProjectListingsCount &&
          query.queryKey[1] === projectId,
      });

      const listingByIdQueryKey: RqQueryKey = [
        QueryKey.ProjectListingById,
        projectListingId,
      ];

      const previousListingById = queryClient.getQueryData<
        ProjectListingResponse | undefined
      >(listingByIdQueryKey);

      const orgFromCaches = findListingOrgFromInfiniteCaches(
        infiniteQueriesSnapshot.map(([, cache]) => cache),
        projectListingId,
      );

      const rollbackWithoutRefetchTargets: Omit<
        MutationRollbackContext,
        'refetchTargets'
      > = {
        companyName: orgFromCaches?.companyName,
        countQueriesSnapshot: [...countQueriesSnapshot],
        infiniteQueriesSnapshot: [...infiniteQueriesSnapshot],
        listingByIdSnapshot: [listingByIdQueryKey, previousListingById],
        organizationId: orgFromCaches?.organizationId,
      };

      const rollback: MutationRollbackContext = {
        ...rollbackWithoutRefetchTargets,
        refetchTargets: [],
      };

      if (!newStage) {
        return rollback;
      }

      const oldStatusId = findListingStatusIdInInfiniteCaches(
        infiniteQueriesSnapshot.map(([, cache]) => cache),
        projectListingId,
      );

      if (
        oldStatusId === undefined ||
        oldStatusId === newProjectListingStageId
      ) {
        return rollback;
      }

      const refetchTargets: Array<{ pageIndex: number; queryKey: RqQueryKey }> =
        [];

      infiniteQueriesSnapshot.forEach(([queryKey]) => {
        const query = queryClient.getQueryCache().find({ queryKey });

        const meta = query ? readProjectListingsQueryMeta(query) : undefined;
        const stageFilter = meta?.stageFilter;

        const shouldRemove =
          stageFilter !== undefined &&
          !stageFilter.includes(newProjectListingStageId);

        queryClient.setQueryData<
          InfiniteData<ProjectListingsResponse | null> | undefined
        >(queryKey, old => {
          if (!old?.pages) return old;

          if (shouldRemove) {
            const pageIndex = old.pages.findIndex(page =>
              page?.records.some(record => record.id === projectListingId),
            );

            if (pageIndex === -1) {
              return old;
            }

            refetchTargets.push({ pageIndex, queryKey });

            return {
              ...old,
              pages: old.pages.map((page, idx) => {
                if (idx !== pageIndex || !page?.records) {
                  return page;
                }

                return {
                  ...page,
                  records: optimisticallyReorderPageRecordsIfApplicable(
                    queryKey,
                    page.records.filter(
                      listing => listing.id !== projectListingId,
                    ),
                    listingStages,
                  ),
                };
              }),
            };
          }

          return {
            ...old,
            pages: old.pages.map(page => {
              if (!page?.records) return page;

              return {
                ...page,
                records: optimisticallyReorderPageRecordsIfApplicable(
                  queryKey,
                  page.records.map(listing =>
                    listing.id === projectListingId
                      ? withUpdatedListingStage(listing, newStage)
                      : listing,
                  ),
                  listingStages,
                ),
              };
            }),
          };
        });
      });

      const rollbackWithTargets: MutationRollbackContext = {
        ...rollbackWithoutRefetchTargets,
        refetchTargets,
      };

      countQueriesSnapshot.forEach(([queryKey, previousCounts]) => {
        if (
          previousCounts === undefined ||
          oldStatusId === undefined ||
          oldStatusId === newProjectListingStageId
        )
          return;

        queryClient.setQueryData<ProjectListingCountResponse | undefined>(
          queryKey,
          optimisticAdjustStagesCount({
            draft: previousCounts,
            fromStageId: oldStatusId,
            toStageId: newProjectListingStageId,
          }),
        );
      });

      queryClient.setQueryData<ProjectListingResponse | undefined>(
        listingByIdQueryKey,
        oldListing =>
          oldListing
            ? {
                ...oldListing,
                listStatusId: newProjectListingStageId,
              }
            : oldListing,
      );

      if (withNotification && orgFromCaches?.companyName) {
        notifyAboutChangedStage(orgFromCaches.companyName, newStage);
      }

      return rollbackWithTargets;
    },
    onSettled: (_data, _err, variables) => {
      if (variables) {
        removePendingProjectListingStage(variables.projectListingId);
      }
    },
    onSuccess: async (_data, variables, rollback): Promise<void> => {
      const { onSuccess } = variables;

      if (projectId) {
        await invalidateCriticalProjectListingQueries({
          projectId,
          refetchTargets: rollback?.refetchTargets ?? [],
        });
      }

      invalidateBackgroundProjectListingQueries({
        organizationId: rollback?.organizationId,
        projectId,
      });

      onSuccess?.();
    },
  });

  const editProjectListingStage = async (
    params: UpdateProjectListingStageParams,
  ): Promise<void> => {
    const {
      newProjectListingStageId,
      onSuccess,
      projectListingId,
      withNotification = true,
    } = params;

    await mutation.mutateAsync({
      newProjectListingStageId,
      onSuccess,
      projectListingId,
      withNotification,
    });
  };

  return {
    editProjectListingStage,
  };
};
